import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  InputNumber,
  Modal,
  Progress,
  Row,
  Slider,
  Space,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  CloudUploadOutlined,
  CopyOutlined,
  DownloadOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload/interface';
import ToolPageHeader from '../../components/ToolPageHeader';
import { useTranslation } from '../../hooks/useTranslation';
import { copyToClipboard } from '../../helpers/clipboard';
import { formatFileSize, getId } from '../../helpers/file';
import './index.css';

const { Dragger } = Upload;
const { Title, Paragraph, Text } = Typography;

interface ConvertResult {
  url: string;
  rawUrl?: string;
  fileName: string;
  storedFileName?: string;
  size?: string;
  sizeBytes?: number;
  startSeconds: number;
  durationSeconds: number;
  fps: number;
  width: number;
}

interface TaskStatusData extends Partial<ConvertResult> {
  taskId: string;
  status: 'RUNNING' | 'SUCCESS' | 'FAILED' | string;
  message?: string;
}

interface ApiResponse<T> {
  success?: boolean;
  message?: string;
  data?: T;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const SUPPORTED_EXTENSIONS = ['mp4', 'mov', 'webm', 'avi', 'm4v', 'mkv'];

const VideoToGif: React.FC = () => {
  const { t } = useTranslation();
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [startSeconds, setStartSeconds] = useState<number>(0);
  const [durationSeconds, setDurationSeconds] = useState<number>(5);
  const [fps, setFps] = useState<number>(10);
  const [width, setWidth] = useState<number>(480);
  const [converting, setConverting] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [taskId, setTaskId] = useState<string>('');
  const [result, setResult] = useState<ConvertResult | null>(null);
  const [resultModalOpen, setResultModalOpen] = useState<boolean>(false);
  const pollingStoppedRef = useRef(false);

  useEffect(() => {
    return () => {
      pollingStoppedRef.current = true;
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const outputTone = useMemo(() => {
    const score = durationSeconds * fps * width;
    if (score >= 72000) {
      return {
        color: 'orange',
        text: t('imageProcessing.tools.videoToGif.outputLarge'),
      };
    }
    if (score >= 36000) {
      return {
        color: 'cyan',
        text: t('imageProcessing.tools.videoToGif.outputBalanced'),
      };
    }
    return {
      color: 'green',
      text: t('imageProcessing.tools.videoToGif.outputCompact'),
    };
  }, [durationSeconds, fps, width, t]);

  const validateFile = (file: RcFile): boolean => {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (!SUPPORTED_EXTENSIONS.includes(extension)) {
      message.error(t('imageProcessing.tools.videoToGif.unsupportedFormat'));
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error(t('imageProcessing.tools.videoToGif.fileTooLarge'));
      return false;
    }
    return true;
  };

  const handleBeforeUpload = (file: RcFile) => {
    if (!validateFile(file)) {
      return Upload.LIST_IGNORE;
    }
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setVideoFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setResult(null);
    setResultModalOpen(false);
    setTaskId('');
    setProgress(0);
    return false;
  };

  const resetVideo = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setVideoFile(null);
    setPreviewUrl('');
    setResult(null);
    setResultModalOpen(false);
    setTaskId('');
    setProgress(0);
  };

  const startMockProgress = () => {
    setProgress(8);
    return window.setInterval(() => {
      setProgress((current) => {
        if (current >= 88) {
          return current;
        }
        return Math.min(88, current + Math.max(2, Math.round((88 - current) / 6)));
      });
    }, 700);
  };

  const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const parseApiResponse = async <T,>(response: Response): Promise<ApiResponse<T>> => {
    const text = await response.text();
    let data: ApiResponse<T> = {};
    try {
      data = text ? JSON.parse(text) as ApiResponse<T> : {};
    } catch {
      throw new Error(t('imageProcessing.tools.videoToGif.convertFailed'));
    }
    if (!response.ok || data.success === false) {
      throw new Error(data.message || t('imageProcessing.tools.videoToGif.convertFailed'));
    }
    return data;
  };

  const buildConvertResult = (task: TaskStatusData): ConvertResult => ({
    url: task.url || '',
    rawUrl: task.rawUrl,
    fileName: task.fileName || 'output.gif',
    storedFileName: task.storedFileName,
    size: task.size,
    sizeBytes: task.sizeBytes,
    startSeconds: task.startSeconds ?? startSeconds,
    durationSeconds: task.durationSeconds ?? durationSeconds,
    fps: task.fps ?? fps,
    width: task.width ?? width,
  });

  const pollTask = async (nextTaskId: string): Promise<ConvertResult> => {
    for (let attempt = 0; attempt < 90; attempt += 1) {
      await sleep(attempt === 0 ? 1000 : 2000);
      if (pollingStoppedRef.current) {
        throw new Error(t('imageProcessing.tools.videoToGif.convertFailed'));
      }

      const response = await fetch(`/img/video-to-gif/tasks/${nextTaskId}`, {
        credentials: 'include',
      });
      const data = await parseApiResponse<TaskStatusData>(response);
      const task = data.data;
      if (!task) {
        throw new Error(t('imageProcessing.tools.videoToGif.convertFailed'));
      }
      if (task.status === 'SUCCESS') {
        if (!task.url) {
          throw new Error(t('imageProcessing.tools.videoToGif.convertFailed'));
        }
        return buildConvertResult(task);
      }
      if (task.status === 'FAILED') {
        throw new Error(task.message || t('imageProcessing.tools.videoToGif.convertFailed'));
      }
    }
    throw new Error(t('imageProcessing.tools.videoToGif.taskTimeout'));
  };

  const convertVideo = async () => {
    if (!videoFile) {
      message.warning(t('imageProcessing.tools.videoToGif.selectVideoFirst'));
      return;
    }

    pollingStoppedRef.current = false;
    const timer = startMockProgress();
    setConverting(true);
    setResult(null);
    setResultModalOpen(false);
    setTaskId('');

    try {
      const formData = new FormData();
      formData.append('file', videoFile);
      formData.append('uid', getId() || '');
      formData.append('startSeconds', String(startSeconds));
      formData.append('durationSeconds', String(durationSeconds));
      formData.append('fps', String(fps));
      formData.append('width', String(width));

      const response = await fetch('/img/video-to-gif/tasks', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const data = await parseApiResponse<TaskStatusData>(response);
      const nextTaskId = data.data?.taskId;
      if (!nextTaskId) {
        throw new Error(t('imageProcessing.tools.videoToGif.convertFailed'));
      }

      setTaskId(nextTaskId);
      message.success(data.message || t('imageProcessing.tools.videoToGif.taskSubmitted'));
      const convertedResult = await pollTask(nextTaskId);
      setProgress(100);
      setResult(convertedResult);
      setResultModalOpen(true);
      message.success(t('imageProcessing.tools.videoToGif.convertComplete'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('imageProcessing.tools.videoToGif.convertFailed');
      message.error(errorMessage);
      setProgress(0);
    } finally {
      pollingStoppedRef.current = true;
      window.clearInterval(timer);
      setConverting(false);
    }
  };

  return (
    <div className="video-to-gif-page">
      <Card className="video-to-gif-card">
        <ToolPageHeader
          backTo="/image-processing"
          backText={t('imageProcessing.tools.videoToGif.backToImageProcessing')}
          title={t('imageProcessing.tools.videoToGif.title')}
          subtitle={t('imageProcessing.tools.videoToGif.subtitle')}
          icon={<VideoCameraOutlined />}
          extra={<Tag color="gold">VIP</Tag>}
        />

        <Row gutter={[24, 24]} className="video-to-gif-workbench">
          <Col xs={24} lg={14}>
            <Card className="video-panel" bordered={false}>
              <div className="panel-heading">
                <div>
                  <Text className="panel-eyebrow">01 / INPUT</Text>
                  <Title level={3}>{t('imageProcessing.tools.videoToGif.uploadTitle')}</Title>
                </div>
                {videoFile && (
                  <Button icon={<ReloadOutlined />} onClick={resetVideo} disabled={converting}>
                    {t('imageProcessing.tools.videoToGif.changeVideo')}
                  </Button>
                )}
              </div>

              {!videoFile ? (
                <Dragger
                  accept="video/mp4,video/quicktime,video/webm,video/x-msvideo,.mp4,.mov,.webm,.avi,.m4v,.mkv"
                  maxCount={1}
                  showUploadList={false}
                  beforeUpload={handleBeforeUpload}
                  disabled={converting}
                  className="video-dropzone"
                >
                  <p className="ant-upload-drag-icon">
                    <CloudUploadOutlined />
                  </p>
                  <p className="ant-upload-text">{t('imageProcessing.tools.videoToGif.uploadHint')}</p>
                  <p className="ant-upload-hint">{t('imageProcessing.tools.videoToGif.uploadSubHint')}</p>
                </Dragger>
              ) : (
                <div className="video-preview-card">
                  <video src={previewUrl} controls className="video-preview" />
                  <div className="video-file-meta">
                    <div>
                      <Text strong>{videoFile.name}</Text>
                      <div className="video-file-detail">
                        {formatFileSize(videoFile.size)} · {videoFile.type || t('imageProcessing.tools.videoToGif.unknownType')}
                      </div>
                    </div>
                    <Tag color={outputTone.color}>{outputTone.text}</Tag>
                  </div>
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Card className="settings-panel" bordered={false}>
              <div className="panel-heading compact">
                <div>
                  <Text className="panel-eyebrow">02 / OUTPUT</Text>
                  <Title level={3}>{t('imageProcessing.tools.videoToGif.settingsTitle')}</Title>
                </div>
              </div>

              <div className="setting-row">
                <div className="setting-label">
                  <Text strong>{t('imageProcessing.tools.videoToGif.startSeconds')}</Text>
                  <Text type="secondary">0-120s</Text>
                </div>
                <InputNumber min={0} max={120} value={startSeconds} onChange={(value) => setStartSeconds(value || 0)} disabled={converting} />
              </div>

              <div className="setting-block">
                <div className="setting-label">
                  <Text strong>{t('imageProcessing.tools.videoToGif.durationSeconds')}</Text>
                  <Text type="secondary">1-15s</Text>
                </div>
                <Slider min={1} max={15} value={durationSeconds} onChange={setDurationSeconds} disabled={converting} />
              </div>

              <div className="setting-block">
                <div className="setting-label">
                  <Text strong>{t('imageProcessing.tools.videoToGif.fps')}</Text>
                  <Text type="secondary">5-15fps</Text>
                </div>
                <Slider min={5} max={15} value={fps} onChange={setFps} disabled={converting} />
              </div>

              <div className="setting-block">
                <div className="setting-label">
                  <Text strong>{t('imageProcessing.tools.videoToGif.width')}</Text>
                  <Text type="secondary">240-720px</Text>
                </div>
                <Slider min={240} max={720} step={20} value={width} onChange={setWidth} disabled={converting} />
              </div>

              <div className="conversion-readout">
                <span>{durationSeconds}s</span>
                <span>{fps}fps</span>
                <span>{width}px</span>
              </div>

              {converting && (
                <div className="convert-progress">
                  <Progress percent={progress} status="active" />
                  <Text type="secondary">{t('imageProcessing.tools.videoToGif.convertingHint')}</Text>
                  {taskId && <div className="task-id">Task ID: {taskId.slice(0, 8)}</div>}
                </div>
              )}

              <Button
                type="primary"
                block
                size="large"
                icon={<PlayCircleOutlined />}
                onClick={convertVideo}
                loading={converting}
                disabled={!videoFile}
                className="convert-button"
              >
                {converting ? t('imageProcessing.tools.videoToGif.converting') : t('imageProcessing.tools.videoToGif.convertButton')}
              </Button>

              {result && !converting && (
                <Button block className="view-result-button" onClick={() => setResultModalOpen(true)}>
                  {t('imageProcessing.tools.videoToGif.viewResult')}
                </Button>
              )}
            </Card>
          </Col>
        </Row>

        {result && (
          <Modal
            open={resultModalOpen}
            footer={null}
            width={900}
            onCancel={() => setResultModalOpen(false)}
            className="video-to-gif-result-modal"
            title={t('imageProcessing.tools.videoToGif.resultTitle')}
          >
            <Row gutter={[24, 24]} align="middle">
              <Col xs={24} md={10}>
                <div className="gif-frame">
                  <img src={result.url} alt={result.fileName} />
                </div>
              </Col>
              <Col xs={24} md={14}>
                <Text className="panel-eyebrow">03 / RESULT</Text>
                <Paragraph type="secondary" className="result-url">{result.url}</Paragraph>
                <Space wrap className="result-tags">
                  <Tag>{result.durationSeconds}s</Tag>
                  <Tag>{result.fps}fps</Tag>
                  <Tag>{result.width}px</Tag>
                  <Tag>{result.size || (result.sizeBytes ? formatFileSize(result.sizeBytes) : '-')}</Tag>
                </Space>
                <Space wrap className="result-actions">
                  <Button icon={<CopyOutlined />} onClick={() => copyToClipboard(result.url, t('imageProcessing.tools.videoToGif.linkCopied'))}>
                    {t('imageProcessing.tools.videoToGif.copyLink')}
                  </Button>
                  <Button icon={<DownloadOutlined />} href={result.url} target="_blank" rel="noreferrer">
                    {t('imageProcessing.tools.videoToGif.openGif')}
                  </Button>
                  <Button type="primary" icon={<ReloadOutlined />} onClick={convertVideo} loading={converting}>
                    {t('imageProcessing.tools.videoToGif.convertAgain')}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Modal>
        )}
      </Card>
    </div>
  );
};

export default VideoToGif;
